/** Structured embeds from agentic v2 personal-worker chat replies. */

export type ChatLinkEmbed = {
  kind: "link";
  target: "mission" | "task" | "thread" | "artifact";
  id: string;
  label: string;
};

export type ChatTaskProposalEmbed = {
  kind: "task_proposal";
  fa_client_id: string;
  mission_id?: string | null;
  catalogue_id: string;
  label: string;
  why?: string;
  proposed_args?: Record<string, unknown>;
  /** Set once the user has accepted the proposal — server-side stamp. */
  created_task_id?: string | null;
  created_mission_id?: string | null;
};

export type EntityPreviewEntityType =
  | "task"
  | "mission"
  | "client"
  | "lead"
  | "case_check"
  | "disclosure"
  | "meeting"
  | "artifact"
  | "portfolio"
  | "product";

export type EntitySearchCandidate = {
  id: string;
  label: string;
  hint?: string;
  status_hint?: string;
  attention?: boolean;
};

export type EntitySearchResultsPresentation = "step" | "deliverable";

export type ChatEntitySearchResultsEmbed = {
  kind: "entity_search_results";
  entity_type: EntityPreviewEntityType;
  resource_label: string;
  title?: string;
  total_count: number;
  truncated: boolean;
  candidates: EntitySearchCandidate[];
  /** Worker-set: intermediate lookup vs user-facing disambiguation outcome. */
  presentation?: EntitySearchResultsPresentation;
};

export type ChatDataTableEmbed = {
  kind: "data_table";
  title?: string;
  columns: string[];
  rows: Array<Record<string, unknown>>;
  units?: string | Record<string, string>;
  as_at?: string;
  source?: string;
  link?: string;
  /** Worker-set: collapsed under tool step vs shown inline. */
  presentation?: EntitySearchResultsPresentation;
};

/** Timeseries stream behind one chart line (e.g. official_net with calculated estimates). */
export type ChatTimeseriesSeriesStream = {
  type?: string | null;
  label?: string | null;
  source?: string | null;
  fallback_from?: string | null;
  last_real_date?: string | null;
  estimate_from?: string | null;
  estimate_label?: string | null;
};

export type ChatTimeseriesPlaygroundOpen = {
  assets: Array<{
    display_name: string;
    resource_id: string;
    resource_type: string;
    stream_type: string;
  }>;
  period?: string;
  start_date?: string;
  end_date?: string;
  frequency?: string;
  display_mode?: "performance" | "index";
};

export type ChatTimeseriesChartEmbed = {
  kind: "timeseries_chart";
  title?: string;
  series: Array<{
    id: string;
    label: string;
    points: Array<{ x: string; y: number }>;
    stream?: ChatTimeseriesSeriesStream;
  }>;
  frequency?: string | null;
  estimate_from?: string | null;
  y_unit?: string;
  as_at?: string;
  source?: string;
  link?: string;
  playground_open?: ChatTimeseriesPlaygroundOpen;
  /** Worker-set: hide standalone card when intermediate vs primary visual. */
  presentation?: EntitySearchResultsPresentation;
};

export type ChatEntityPreviewEmbed = {
  kind: "entity_preview";
  entity_type: EntityPreviewEntityType;
  resource_label?: string;
  id: string;
  label: string;
  status: string;
  status_display?: string;
  subtitle?: string;
  summary?: string;
  attention?: boolean;
  progress_pct?: number;
  meta?: Record<string, string>;
};

export type ChatEmbed =
  | ChatLinkEmbed
  | ChatTaskProposalEmbed
  | ChatEntitySearchResultsEmbed
  | ChatEntityPreviewEmbed
  | ChatDataTableEmbed
  | ChatTimeseriesChartEmbed;

function isEntityPreviewEntityType(value: unknown): value is EntityPreviewEntityType {
  return (
    value === "task" ||
    value === "mission" ||
    value === "client" ||
    value === "lead" ||
    value === "case_check" ||
    value === "disclosure" ||
    value === "meeting" ||
    value === "artifact" ||
    value === "portfolio" ||
    value === "product"
  );
}


function isDataTableEmbed(item: unknown): item is ChatDataTableEmbed {
  if (!item || typeof item !== "object") return false;
  const row = item as ChatDataTableEmbed;
  return (
    row.kind === "data_table" &&
    Array.isArray(row.columns) &&
    row.columns.length > 0 &&
    Array.isArray(row.rows)
  );
}

function isTimeseriesChartEmbed(item: unknown): item is ChatTimeseriesChartEmbed {
  if (!item || typeof item !== "object") return false;
  const row = item as ChatTimeseriesChartEmbed;
  return (
    row.kind === "timeseries_chart" &&
    Array.isArray(row.series) &&
    row.series.length > 0 &&
    row.series.every(
      (series) =>
        series &&
        typeof series === "object" &&
        typeof series.id === "string" &&
        typeof series.label === "string" &&
        Array.isArray(series.points),
    )
  );
}

/** Validate untrusted embed payloads (HTTP/WS) into known embed shapes. */
export function parseChatEmbeds(raw: unknown): ChatEmbed[] {
  if (!Array.isArray(raw)) return [];
  const out: ChatEmbed[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const kind = (item as ChatEmbed).kind;
    if (kind === "link" && (item as ChatLinkEmbed).target && (item as ChatLinkEmbed).id && (item as ChatLinkEmbed).label) {
      out.push(item as ChatLinkEmbed);
    } else if (
      kind === "task_proposal" &&
      (item as ChatTaskProposalEmbed).fa_client_id &&
      (item as ChatTaskProposalEmbed).catalogue_id &&
      (item as ChatTaskProposalEmbed).label
    ) {
      out.push(item as ChatTaskProposalEmbed);
    } else if (kind === "entity_search_results") {
      const search = item as ChatEntitySearchResultsEmbed;
      if (
        isEntityPreviewEntityType(search.entity_type) &&
        search.resource_label &&
        Array.isArray(search.candidates) &&
        search.candidates.length > 0 &&
        search.candidates.every(
          (candidate) =>
            candidate &&
            typeof candidate === "object" &&
            typeof candidate.id === "string" &&
            typeof candidate.label === "string",
        )
      ) {
        const presentation = (search as ChatEntitySearchResultsEmbed).presentation;
        out.push({
          ...search,
          ...(presentation === "step" || presentation === "deliverable" ? { presentation } : {}),
        } as ChatEntitySearchResultsEmbed);
      }
    } else if (kind === "entity_preview") {
      const preview = item as ChatEntityPreviewEmbed;
      const entityType = preview.entity_type;
      if (preview.id && preview.label && preview.status && isEntityPreviewEntityType(entityType)) {
        out.push(preview);
      }
    } else if (isDataTableEmbed(item)) {
      const table = item as ChatDataTableEmbed;
      const presentation = table.presentation;
      out.push({
        ...table,
        ...(presentation === "step" || presentation === "deliverable" ? { presentation } : {}),
      });
    } else if (isTimeseriesChartEmbed(item)) {
      const chart = item as ChatTimeseriesChartEmbed;
      const presentation = chart.presentation;
      out.push({
        ...chart,
        ...(presentation === "step" || presentation === "deliverable" ? { presentation } : {}),
      });
    }
  }
  return out;
}
